/**
 * LeerpuntToevoegenButton — zelf een leerpunt schrijven bij het naluisteren.
 *
 * Zit op het gespreksdetail, naast Maak opvolgtaak. De gesprekscoach ziet niet
 * alles: bij WB-322232 vond hij één punt over statusvelden lezen, terwijl de
 * echte les ging over de volgorde tussen zelf plannen en een keuzelink sturen.
 * Zonder dit knopje landt zo'n observatie nergens.
 *
 * POST /api/telephony/leerpunten met bron='mens'. Het leerpunt komt NIET in de
 * voorgesteld-wachtrij maar direct op 'akkoord' (je keurt jezelf niet goed);
 * fixtype 'kb' gaat meteen door naar een actief kennisbank-artikel.
 */

import { useState } from 'react';
import { supabase } from '../../lib/supabase';

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

type Fixtype = 'prompt' | 'kb' | 'verb' | 'bug';

const FIXTYPES: Array<{ key: Fixtype; label: string; uitleg: string }> = [
  { key: 'prompt', label: 'Promptregel', uitleg: 'gedrag, toon of volgorde — doorvoeren in een werksessie' },
  { key: 'kb', label: 'Kennisbank', uitleg: 'feitenkennis — wordt direct een actief artikel' },
  { key: 'verb', label: 'Verb/tool', uitleg: 'een handeling die ontbreekt of verkeerd gaat — doorvoeren als PR' },
  { key: 'bug', label: 'Bug', uitleg: 'technisch defect — uitzoeken in een werksessie' },
];

/** Zelfde vorm als de coach per fixtype produceert (zie migratie 121). */
const VELDEN: Record<Fixtype, Array<{ key: string; label: string; lang?: boolean; placeholder?: string }>> = {
  prompt: [
    { key: 'beschrijving', label: 'Wat ging er mis en waarom fixt een promptregel dit?', lang: true },
    {
      key: 'voorstel_tekst',
      label: 'Voorgestelde tekst (zo komt het in de prompt)',
      lang: true,
      placeholder: 'Probeer een afspraak altijd eerst zelf in te plannen; lukt dat niet, stuur dan pas de link naar de persoonlijke pagina.',
    },
  ],
  kb: [
    { key: 'brand', label: 'Merk', placeholder: 'KLEDA' },
    { key: 'titel', label: 'Titel' },
    { key: 'vraag_patronen', label: 'Vraagpatronen (één per regel)', lang: true },
    { key: 'antwoord', label: 'Antwoord (kanaal-neutraal, geen aanhef)', lang: true },
  ],
  verb: [
    { key: 'verb', label: 'Verb', placeholder: 'plan_afspraak of "nieuw"' },
    { key: 'observatie', label: 'Observatie', lang: true },
  ],
  bug: [{ key: 'beschrijving', label: 'Beschrijving', lang: true }],
};

interface Props {
  callId: string;
  /** Voorgevuld bewijs, bv. een transcriptfragment of een agent-actie. */
  defaultBewijs?: string | null;
  brand?: string | null;
  className?: string;
  onCreated?: () => void;
}

export function LeerpuntToevoegenButton({ callId, defaultBewijs, brand, className, onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [fixtype, setFixtype] = useState<Fixtype>('prompt');
  const [leerpunt, setLeerpunt] = useState('');
  const [bewijs, setBewijs] = useState(defaultBewijs ?? '');
  const [velden, setVelden] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gelukt, setGelukt] = useState<string | null>(null);

  const reset = () => {
    setFixtype('prompt');
    setLeerpunt('');
    setBewijs(defaultBewijs ?? '');
    setVelden({});
    setError(null);
    setGelukt(null);
  };

  const kiesFixtype = (t: Fixtype) => {
    setFixtype(t);
    // Merk voorvullen zodat een kb-voorstel niet op de brand-check strandt.
    setVelden(t === 'kb' && brand ? { brand: brand.toUpperCase() } : {});
  };

  const submit = async () => {
    if (!leerpunt.trim()) {
      setError('Beschrijf in één zin wat er beter moet');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const voorstel: Record<string, string> = {};
      for (const [k, v] of Object.entries(velden)) if (v.trim()) voorstel[k] = v.trim();
      const r = await fetch('/api/telephony/leerpunten', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          callId,
          fixtype,
          leerpunt: leerpunt.trim(),
          bewijs: bewijs.trim() || undefined,
          voorstel: Object.keys(voorstel).length > 0 ? voorstel : undefined,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `opslaan mislukt: ${r.status}`);
      setGelukt(
        j.status === 'toegepast'
          ? 'Opgeslagen en meteen als actief kennisbank-artikel gepubliceerd.'
          : 'Opgeslagen op akkoord — doorvoeren gebeurt in een werksessie.',
      );
      onCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        className={
          className ??
          'px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded'
        }
      >
        Leerpunt toevoegen
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          onClick={() => !saving && setOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-slate-900 mb-1">Leerpunt toevoegen</h3>
            <p className="text-xs text-slate-500 mb-3">
              Wat jij zelf hoort bij het naluisteren. Komt direct op akkoord te staan, niet in de reviewwachtrij.
            </p>

            {error && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
            )}
            {gelukt ? (
              <>
                <div className="mb-4 px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded text-sm">
                  {gelukt}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded"
                  >
                    Sluiten
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className="block text-xs font-medium text-slate-600 mb-1">Soort</label>
                <div className="flex flex-wrap gap-1 mb-3">
                  {FIXTYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      title={t.uitleg}
                      onClick={() => kiesFixtype(t.key)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        fixtype === t.key
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mb-3">{FIXTYPES.find((t) => t.key === fixtype)?.uitleg}</p>

                <label className="block text-xs font-medium text-slate-600 mb-1">Leerpunt (één zin) *</label>
                <textarea
                  value={leerpunt}
                  onChange={(e) => setLeerpunt(e.target.value)}
                  rows={2}
                  placeholder="Bijv. De agent stuurde meteen de keuzelink terwijl hij de afspraak ook zelf had kunnen inplannen."
                  className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  autoFocus
                />

                <label className="block text-xs font-medium text-slate-600 mb-1">Bewijs (citaat of agent-actie)</label>
                <textarea
                  value={bewijs}
                  onChange={(e) => setBewijs(e.target.value)}
                  rows={2}
                  className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />

                {VELDEN[fixtype].map(({ key, label, lang, placeholder }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                    {lang ? (
                      <textarea
                        value={velden[key] ?? ''}
                        onChange={(e) => setVelden((v) => ({ ...v, [key]: e.target.value }))}
                        rows={3}
                        placeholder={placeholder}
                        className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                    ) : (
                      <input
                        type="text"
                        value={velden[key] ?? ''}
                        onChange={(e) => setVelden((v) => ({ ...v, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full mb-3 px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                    )}
                  </div>
                ))}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => !saving && setOpen(false)}
                    className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 text-sm rounded"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={() => void submit()}
                    disabled={saving}
                    className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded"
                  >
                    {saving ? 'Bezig…' : 'Opslaan'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
